import React, { memo, useMemo } from 'react';
import { cn } from '@/lib/utils';
import type { LightingFixture } from '@/types/floorPlanViewer';

interface Point {
  x: number;
  y: number;
}

export interface StripLightingMarkerProps {
  /** The strip fixture data */
  strip: LightingFixture;
  /** Whether the strip is selected */
  isSelected?: boolean;
  /** Whether the strip is hovered */
  isHovered?: boolean;
  /** Whether the strip is lit during simulation */
  isSimLit?: boolean;
  /** Whether to dim during simulation (not lit) */
  shouldDimDuringSimulation?: boolean;
  /** Whether in circuit mode */
  isCircuitMode?: boolean;
  /** Whether being dragged */
  isBeingDragged?: boolean;
  /** Current zoom level */
  zoom?: number;
  /** Circuit color if assigned */
  circuitColor?: string;
  /** Circuit sequence number for badge */
  circuitSequenceNumber?: number;
  /** Whether circuit has a parent circuit */
  hasParentCircuit?: boolean;
  /** Whether strip is in a selected circuit */
  isInSelectedCircuit?: boolean;
  /** Whether strip is in a hovered circuit */
  isInHoveredCircuit?: boolean;
  /** Whether circuit badge should be highlighted (red) */
  circuitBadgeHighlighted?: boolean;
  /** Click handler */
  onClick?: (e: React.MouseEvent) => void;
  /** Mouse down handler for drag */
  onMouseDown?: (e: React.MouseEvent) => void;
  /** Mouse enter handler */
  onMouseEnter?: () => void;
  /** Mouse leave handler */
  onMouseLeave?: () => void;
  /** Handler for endpoint drag start */
  onEndpointDragStart?: (endpoint: 'start' | 'end', e: React.MouseEvent) => void;
  /** Handler for vertex drag (for polylines) */
  onVertexDragStart?: (index: number, e: React.MouseEvent) => void;
  /** Marker opacity from AI tools (0-1 range) */
  markerOpacity?: number;
  /** Live dragged endpoint position for visual feedback during endpoint drag */
  draggedEndpoint?: { endpoint: 'start' | 'end'; x: number; y: number };
  /** Live dragged vertex position for visual feedback during polyline vertex drag */
  draggedVertex?: { index: number; x: number; y: number };
}

/**
 * StripLightingMarker renders a dashed line representing an LED strip fixture.
 * Supports both simple two-point strips and multi-point polyline strips.
 * It supports selection, glow effects during simulation, and resize handles.
 *
 * ARCHITECTURE (Round 6 - matches FixtureLayer/LVProductLayer pattern):
 * - Wrapper div positioned at CENTER of strip geometry
 * - Uses translate(-50%, -50%) for proper centering (zoom-stable)
 * - Wrapper is non-interactive so its invisible bounding box cannot capture clicks
 * - A transparent SVG stroke path captures interactions only along the strip line
 */
export const StripLightingMarker = memo<StripLightingMarkerProps>(
  ({
    strip,
    isSelected = false,
    isHovered = false,
    isSimLit = false,
    shouldDimDuringSimulation = false,
    isCircuitMode: _isCircuitMode = false,
    isBeingDragged = false,
    zoom = 1,
    circuitColor,
    circuitSequenceNumber,
    hasParentCircuit: _hasParentCircuit,
    isInSelectedCircuit = false,
    isInHoveredCircuit = false,
    circuitBadgeHighlighted = false,
    onClick,
    onMouseDown,
    onMouseEnter,
    onMouseLeave,
    onEndpointDragStart,
    onVertexDragStart,
    draggedEndpoint,
    draggedVertex,
    markerOpacity = 1,
  }) => {
    // Determine if this is a polyline strip (has strip_points array)
    const isPolyline = strip.strip_points && strip.strip_points.length > 1;

    // Get all points - either from strip_points or from start/end coordinates
    // When draggedEndpoint is present, substitute the dragged endpoint's live position
    const points: Point[] = useMemo(() => {
      let pts: Point[];
      if (strip.strip_points && strip.strip_points.length > 1) {
        pts = [...strip.strip_points];
      } else {
        pts = [
          { x: strip.x_position, y: strip.y_position },
          {
            x: strip.end_x_position ?? strip.x_position,
            y: strip.end_y_position ?? strip.y_position,
          },
        ];
      }
      // Substitute dragged endpoint for live visual feedback
      if (draggedEndpoint) {
        if (draggedEndpoint.endpoint === 'start') {
          pts[0] = { x: draggedEndpoint.x, y: draggedEndpoint.y };
        } else {
          pts[pts.length - 1] = { x: draggedEndpoint.x, y: draggedEndpoint.y };
        }
      }
      if (draggedVertex && draggedVertex.index >= 0 && draggedVertex.index < pts.length) {
        pts[draggedVertex.index] = { x: draggedVertex.x, y: draggedVertex.y };
      }
      return pts;
    }, [
      strip.strip_points,
      strip.x_position,
      strip.y_position,
      strip.end_x_position,
      strip.end_y_position,
      draggedEndpoint,
      draggedVertex,
    ]);

    // Calculate bounding box from all points
    const bounds = useMemo(() => {
      if (points.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };

      let minX = points[0].x;
      let minY = points[0].y;
      let maxX = points[0].x;
      let maxY = points[0].y;

      for (const point of points) {
        minX = Math.min(minX, point.x);
        minY = Math.min(minY, point.y);
        maxX = Math.max(maxX, point.x);
        maxY = Math.max(maxY, point.y);
      }

      return { minX, minY, maxX, maxY };
    }, [points]);

    // CENTER-BASED POSITIONING (matches FixtureLayer pattern)
    // Calculate center of strip geometry for stable zoom behavior
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;

    // Determine stroke color
    const getStrokeColor = () => {
      // Warm cream-white when lit - matches fixture glow background color
      // Using rgba(255, 241, 224) = #fff1e0 for consistency with fixture glow
      if (isSimLit) return '#fff1e0';
      // Red for selected/hovered circuit (blinking state)
      if (isInSelectedCircuit || isInHoveredCircuit) return '#ef4444'; // red-500
      if (circuitColor && circuitColor.toLowerCase() !== '#ffffff') return circuitColor;
      if (strip.circuit_id) return '#10b981'; // emerald-500
      return '#fbbf24'; // amber-400 - default LED strip color
    };

    // Handle size based on zoom and scale (scale affects thickness only)
    const handleRadius = Math.max(6, 8 / zoom);
    const baseStrokeWidth = Math.max(3, 4 / zoom);
    const strokeWidth = baseStrokeWidth * (strip.scale || 1.0);

    // Dynamic padding - scales with stroke width to prevent visual clipping when scaled up
    // No extra padding for glow — SVG overflow="visible" lets glow extend beyond wrapper bounds
    // This keeps the clickable area tight around the strip path
    const padding = Math.max(20, strokeWidth * 4);
    const svgWidth = bounds.maxX - bounds.minX + padding * 2;
    const svgHeight = bounds.maxY - bounds.minY + padding * 2;

    // Absolute filter bounds for userSpaceOnUse (prevents zero-bounding-box collapse on horizontal/vertical strips)
    // Margin just needs to exceed the blur radius + stroke extent so glow isn't clipped by filter region
    const glowMargin = 200;
    const filterX = -glowMargin;
    const filterY = -glowMargin;
    const filterWidth = svgWidth + glowMargin * 2;
    const filterHeight = svgHeight + glowMargin * 2;

    // Opacity based on state (matches FixtureLayer pattern)
    const getOpacity = () => {
      if (isSimLit) return 1;
      if (shouldDimDuringSimulation) return 0.4;
      if (isInHoveredCircuit) return 1;
      if (isHovered) return 1;
      return markerOpacity;
    };

    // Build SVG path string from points using LOCAL coordinates
    // SVG is positioned with center at (0,0), so we offset all points accordingly
    const buildPathD = (pts: Point[]) => {
      if (pts.length < 2) return '';
      const [first, ...rest] = pts;
      // Convert to local coordinates (relative to SVG top-left which is at center - svgWidth/2, center - svgHeight/2)
      const svgOffsetX = centerX - svgWidth / 2;
      const svgOffsetY = centerY - svgHeight / 2;
      return `M ${first.x - svgOffsetX} ${first.y - svgOffsetY} ${rest.map(p => `L ${p.x - svgOffsetX} ${p.y - svgOffsetY}`).join(' ')}`;
    };

    const pathD = buildPathD(points);

    // For the middle handle on simple strips (in local SVG coordinates)
    const svgOffsetX = centerX - svgWidth / 2;
    const svgOffsetY = centerY - svgHeight / 2;
    const midX = (points[0].x + points[points.length - 1].x) / 2;
    const midY = (points[0].y + points[points.length - 1].y) / 2;

    // Convert point coordinates to local SVG coordinates
    const toLocalX = (x: number) => x - svgOffsetX;
    const toLocalY = (y: number) => y - svgOffsetY;

    return (
      // WRAPPER DIV - Positioned at TOP-LEFT of SVG bounding box
      // Non-interactive: the strip's transparent path below owns hit testing.
      <div
        data-fixture-id={strip.id}
        className={cn('absolute', isBeingDragged && 'z-[200]')}
        style={{
          // Position at the actual top-left of the SVG area (no translate needed)
          left: svgOffsetX,
          top: svgOffsetY,
          // Explicit width/height to match SVG dimensions
          width: svgWidth,
          height: svgHeight,
          pointerEvents: 'none',
          // Explicit transparent background to prevent white rectangle during drag
          background: 'transparent',
          // Must be visible to allow glow to extend beyond the wrapper bounds
          overflow: 'visible',
          // Lit strips render above dimmed strips so glow is visible
          // Dragged strips should be on top
          // Higher z-index when lit (500) to ensure glow is visible above other elements (matches fixtures)
          zIndex: isBeingDragged ? 200 : isSimLit ? 500 : isSelected ? 50 : undefined,
        }}
      >
        <svg
          overflow="visible"
          style={{
            width: svgWidth,
            height: svgHeight,
            // SVG fills the wrapper exactly - no transforms or margins needed
            // Explicit transparent background to prevent white rectangle
            background: 'transparent',
            pointerEvents: 'none',
          }}
        >
          {/* Selection highlight (under the strip) */}
          {isSelected && pathD && (
            <path
              d={pathD}
              fill="none"
              stroke="#3b82f6"
              strokeWidth={strokeWidth + 6}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity={0.4}
              className="pointer-events-none"
            />
          )}

          {/* Hit area: capture only along the strip path, not the SVG bounding rectangle. */}
          {pathD && (
            <path
              d={pathD}
              fill="none"
              stroke="transparent"
              strokeWidth={Math.max(strokeWidth + 10, 12 / zoom)}
              strokeLinecap="round"
              strokeLinejoin="round"
              pointerEvents="stroke"
              style={{ cursor: isBeingDragged ? 'grabbing' : 'pointer' }}
              onClick={onClick}
              onMouseDown={onMouseDown}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            />
          )}

          {/* Warm glow effect when simulation is running - SVG blur filter */}
          {/* Matches fixture box-shadow: tight, bright, warm glow */}
          {/* Uses userSpaceOnUse with absolute pixel bounds to prevent zero-bounding-box collapse on horizontal/vertical strips */}
          {isSimLit && pathD && (
            <>
              <defs>
                <filter
                  id={`strip-glow-far-${strip.id}`}
                  filterUnits="userSpaceOnUse"
                  x={filterX}
                  y={filterY}
                  width={filterWidth}
                  height={filterHeight}
                >
                  <feGaussianBlur stdDeviation="40" />
                </filter>
                <filter
                  id={`strip-glow-near-${strip.id}`}
                  filterUnits="userSpaceOnUse"
                  x={filterX}
                  y={filterY}
                  width={filterWidth}
                  height={filterHeight}
                >
                  <feGaussianBlur stdDeviation="12" />
                </filter>
              </defs>
              {/* Layer 3 - outer warm ambient (matches fixture ~180px box-shadow) */}
              <path
                d={pathD}
                fill="none"
                stroke="rgba(255, 214, 170, 0.25)"
                strokeWidth={strokeWidth * 8}
                strokeLinecap="round"
                strokeLinejoin="round"
                filter={`url(#strip-glow-far-${strip.id})`}
                className="pointer-events-none"
                style={{ animation: 'light-on 0.6s ease-out forwards' }}
              />
              {/* Layer 2 - bright inner glow (matches fixture ~120px box-shadow) */}
              <path
                d={pathD}
                fill="none"
                stroke="rgba(255, 228, 196, 0.5)"
                strokeWidth={strokeWidth * 4}
                strokeLinecap="round"
                strokeLinejoin="round"
                filter={`url(#strip-glow-near-${strip.id})`}
                className="pointer-events-none"
                style={{ animation: 'light-on 0.6s ease-out forwards' }}
              />
              {/* Layer 1 - core glow, closest to the strip (matches fixture ~60px box-shadow) */}
              <path
                d={pathD}
                fill="none"
                stroke="rgba(255, 241, 224, 0.7)"
                strokeWidth={strokeWidth * 2}
                strokeLinecap="round"
                strokeLinejoin="round"
                filter={`url(#strip-glow-near-${strip.id})`}
                className="pointer-events-none"
                style={{ animation: 'light-on 0.6s ease-out forwards' }}
              />
            </>
          )}

          {/* Main strip path */}
          {pathD && (
            <path
              d={pathD}
              fill="none"
              stroke={getStrokeColor()}
              strokeWidth={isSimLit ? strokeWidth * 1.5 : strokeWidth}
              strokeDasharray={isSimLit ? 'none' : '12 6'}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={getOpacity()}
              className="pointer-events-none"
              style={{
                // Only transition specific properties to prevent zoom jitter
                // (transitioning 'all' causes stroke-width changes during zoom to animate)
                transition: 'opacity 0.3s ease, stroke 0.3s ease',
                // IMPORTANT: isSimLit takes priority over circuit selection
                // During simulation, strips should GLOW (not blink)
                // Blinking only happens when circuit/fixture is selected OUTSIDE of simulation
                animation: isSimLit
                  ? 'light-on 0.6s ease-out forwards'
                  : isInSelectedCircuit || isInHoveredCircuit
                    ? 'slow-blink 0.8s ease-in-out infinite'
                    : undefined,
              }}
            />
          )}

          {/* Circuit badge (when assigned to circuit) - matches fixture badge style */}
          {circuitSequenceNumber !== undefined &&
            (() => {
              // Badge sizing that scales naturally with zoom (matching fixture badge behavior)
              // Use fixed SVG units - they will scale with zoom like the rest of the floor plan
              const badgeWidth = 16;
              const badgeHeight = 14;
              const badgeFontSize = 10;
              const badgeOffsetY = 20; // Distance above the strip start point

              return (
                <>
                  {/* Shadow filter for badge */}
                  <defs>
                    <filter
                      id={`badge-shadow-${strip.id}`}
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.25" />
                    </filter>
                  </defs>
                  {/* Rounded rect badge like fixtures - using local coordinates */}
                  <rect
                    x={toLocalX(points[0].x) - badgeWidth / 2}
                    y={toLocalY(points[0].y) - badgeOffsetY - badgeHeight / 2}
                    width={badgeWidth}
                    height={badgeHeight}
                    rx={2}
                    fill={circuitBadgeHighlighted ? '#fef2f2' : 'white'}
                    stroke={circuitBadgeHighlighted ? '#ef4444' : '#9ca3af'}
                    strokeWidth={1.5}
                    filter={`url(#badge-shadow-${strip.id})`}
                    className="pointer-events-none"
                    style={{
                      animation: circuitBadgeHighlighted
                        ? 'slow-blink 0.8s ease-in-out infinite'
                        : undefined,
                    }}
                  />
                  {/* Badge number */}
                  <text
                    x={toLocalX(points[0].x)}
                    y={toLocalY(points[0].y) - badgeOffsetY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={circuitBadgeHighlighted ? '#dc2626' : '#374151'}
                    fontSize={badgeFontSize}
                    fontWeight={700}
                    fontFamily="system-ui, -apple-system, sans-serif"
                    className="pointer-events-none"
                    style={{
                      animation: circuitBadgeHighlighted
                        ? 'slow-blink 0.8s ease-in-out infinite'
                        : undefined,
                    }}
                  >
                    {circuitSequenceNumber}
                  </text>
                </>
              );
            })()}

          {/* Vertex handles (only visible when selected) - using local coordinates */}
          {isSelected && (
            <>
              {/* All vertex handles for polylines */}
              {points.map((point, index) => {
                const isStartOrEnd = index === 0 || index === points.length - 1;
                return (
                  <circle
                    key={index}
                    cx={toLocalX(point.x)}
                    cy={toLocalY(point.y)}
                    r={handleRadius}
                    fill={isStartOrEnd ? 'white' : '#e5e7eb'}
                    stroke="#3b82f6"
                    strokeWidth={2}
                    className="pointer-events-auto cursor-move"
                    onMouseDown={e => {
                      e.stopPropagation();
                      if (isPolyline && onVertexDragStart) {
                        onVertexDragStart(index, e);
                      } else if (index === 0) {
                        onEndpointDragStart?.('start', e);
                      } else if (index === points.length - 1) {
                        onEndpointDragStart?.('end', e);
                      }
                    }}
                  />
                );
              })}

              {/* Middle handle for repositioning (only for simple strips) */}
              {!isPolyline && (
                <circle
                  cx={toLocalX(midX)}
                  cy={toLocalY(midY)}
                  r={handleRadius - 1}
                  fill="#3b82f6"
                  stroke="white"
                  strokeWidth={2}
                  className="pointer-events-auto cursor-grab"
                  onMouseDown={onMouseDown}
                />
              )}
            </>
          )}

          {/* Hover indicator (small circles at start/end points) - using local coordinates */}
          {isHovered && !isSelected && (
            <>
              <circle
                cx={toLocalX(points[0].x)}
                cy={toLocalY(points[0].y)}
                r={4}
                fill={getStrokeColor()}
                opacity={0.6}
                className="pointer-events-none"
              />
              <circle
                cx={toLocalX(points[points.length - 1].x)}
                cy={toLocalY(points[points.length - 1].y)}
                r={4}
                fill={getStrokeColor()}
                opacity={0.6}
                className="pointer-events-none"
              />
            </>
          )}
        </svg>
      </div>
    );
  },
);

StripLightingMarker.displayName = 'StripLightingMarker';

export default StripLightingMarker;
